import CONDITIONS from '../../config/conditions';

const USE_TYPES = [
  'Daily commuter',
  'Weekend fun',
  'Spirited driving',
  'Track days',
  'Car shows / Concours',
  'Long-distance touring',
  'Collection / Investment',
  'Occasional / Seasonal use',
  'Project car / Restoration',
];

export default function StepContext({ data, update, onNext, onBack }) {
  const uses = Array.isArray(data.primaryUse) ? data.primaryUse : (data.primaryUse ? [data.primaryUse] : []);
  const isFactoryNew = data.isFactoryNew || false;

  // Need at least one use; also need last service date unless factory-new
  const canProceed = uses.length > 0 && (isFactoryNew || data.lastService);

  function toggleUse(use) {
    const next = uses.includes(use) ? uses.filter((u) => u !== use) : [...uses, use];
    update({ primaryUse: next });
  }

  function handleFactoryNewToggle(checked) {
    update({
      isFactoryNew: checked,
      lastService: checked ? new Date().toISOString().split('T')[0] : '',
      lastServiceDescription: checked ? '' : data.lastServiceDescription,
    });
  }

  return (
    <>
      <div className="screen-header">
        <h1 className="screen-title">A Bit More Context</h1>
        <p className="screen-subtitle">
          This helps us calibrate recommendations to your experience and driving habits.
        </p>
      </div>

      <div style={{ flex: 1, paddingTop: 24 }}>
        {/* Primary Use */}
        <div className="form-group">
          <label className="form-label">Primary Use <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(select all that apply)</span></label>
          <div className="chip-group">
            {USE_TYPES.map((use) => (
              <button
                key={use}
                className={`chip ${uses.includes(use) ? 'selected' : ''}`}
                onClick={() => toggleUse(use)}
              >
                {use}
              </button>
            ))}
          </div>
        </div>

        {/* Current Condition */}
        <div className="form-group">
          <label className="form-label">Current Condition</label>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {CONDITIONS.map((cond) => (
              <button
                key={cond.value}
                onClick={() => update({ condition: cond.value })}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '12px 14px',
                  borderRadius: 'var(--radius-sm)',
                  border: data.condition === cond.value
                    ? '1.5px solid var(--accent)'
                    : '1.5px solid var(--border)',
                  background: data.condition === cond.value
                    ? 'var(--accent-subtle)'
                    : 'var(--bg-elevated)',
                  textAlign: 'left',
                  transition: 'all 0.15s ease',
                  cursor: 'pointer',
                }}
              >
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: data.condition === cond.value
                      ? '5px solid var(--accent)'
                      : '2px solid var(--text-muted)',
                    flexShrink: 0,
                    marginTop: 1,
                    transition: 'border 0.15s ease',
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                    {cond.value}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.3 }}>
                    {cond.description}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Service History Section */}
        <div
          style={{
            height: 1,
            background: 'var(--border)',
            margin: '8px 0 20px',
          }}
        />

        {/* Last Service Date — hidden when factory-new */}
        {!isFactoryNew && (
          <>
            <div className="form-group">
              <label className="form-label">When was your last service?</label>
              <input
                className="form-input"
                type="date"
                value={data.lastService}
                onChange={(e) => update({ lastService: e.target.value })}
                style={{ colorScheme: 'dark' }}
              />
              <p style={{ color: 'var(--text-muted)', fontSize: '0.6875rem', marginTop: 4 }}>
                Approximate date is fine
              </p>
            </div>

            {/* What was done */}
            <div className="form-group">
              <label className="form-label">Last service performed</label>
              <input
                className="form-input"
                type="text"
                placeholder="e.g., Oil change, tire rotation"
                value={data.lastServiceDescription || ''}
                onChange={(e) => update({ lastServiceDescription: e.target.value })}
              />
            </div>
          </>
        )}

        {/* Factory-new bypass — positioned after service fields */}
        <div
          style={{
            padding: '14px 16px',
            borderRadius: 'var(--radius-sm)',
            border: `1.5px solid ${isFactoryNew ? 'var(--accent)' : 'var(--border)'}`,
            background: isFactoryNew ? 'var(--accent-subtle)' : 'var(--bg-input)',
            marginBottom: 20,
            transition: 'all 0.2s',
          }}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={isFactoryNew}
              onChange={(e) => handleFactoryNewToggle(e.target.checked)}
              style={{
                width: 18,
                height: 18,
                accentColor: 'var(--accent)',
                flexShrink: 0,
              }}
            />
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                This is a factory-new vehicle with no service history
              </div>
              {isFactoryNew && (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  Service history fields skipped
                </div>
              )}
            </div>
          </label>
        </div>
      </div>

      <div className="nav-row">
        <button className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        <button className="btn btn-primary" disabled={!canProceed} onClick={onNext}>
          Continue
        </button>
      </div>
    </>
  );
}
